<template>
  <div class="pets-policy-details__layout">
    <div class="pets-policy-details__main-section">
      <WhiteButton
        class="pets-policy-details__fill-data"
        @click="onOpenFillDataPopup"
      >
        <FillDataIcon
          class="pets-policy-details__fill-data__icon"
        />
        <HelpTooltip
          class="pets-policy-details__fill-data__tooltip"
          text="Заповнення форми"
        />
      </WhiteButton>

      <PolicyholderForm
        ref="policyholderForm"
        :policyholder-tax-number="policyholder.taxNumber"
        :policyholder-last-name="getLastNameUa"
        :policyholder-first-name="getFirstNameUa"
        :policyholder-middle-name="getMiddleNameUa"
        :policyholder-birthday="formatDate(policyholder.dateOfBirth)"
        :policyholder-address-city="getPolicyholderAddressCity"
        :policyholder-address-street="getPolicyholderAddressStreet"
        :policyholder-address-house="getPolicyholderAddressHouse"
        :policyholder-address-apartment="getPolicyholderAddressApartment"
        :policyholder-phone="policyholder.phone"
        :policyholder-email="policyholder.email"
        :tax-number-status="getTaxNumberStatus"
        :without-tax-number="policyholder.withoutTaxNumber"
        @changePolicyholder="onChangePolicyholder"
        @changePolicyholderTaxNumber="onChangePolicyholderTaxNumber"
        @changePolicyholderLastName="onChangePolicyholderLastName"
        @changePolicyholderFirstName="onChangePolicyholderFirstName"
        @changePolicyholderMiddleName="onChangePolicyholderMiddleName"
        @changePolicyholderBirthday="onChangePolicyholderBirthday"
        @changePolicyholderAddressCity="onChangePolicyholderAddressCity"
        @changePolicyholderAddressStreet="onChangePolicyholderAddressStreet"
        @changePolicyholderAddressHouse="onChangePolicyholderAddressHouse"
        @changePolicyholderAddressApartment="onChangePolicyholderAddressApartment"
        @changePolicyholderPhone="onChangePolicyholderPhone"
        @changePolicyholderEmail="onChangePolicyholderEmail"
        @changeWithoutTaxNumber="onChangeWithoutTaxNumber"
      />

      <DocumentForm
        v-if="policyholder.type === 'natural'"
        ref="documentForm"
        :policyholder-birthday="formatDate(policyholder.dateOfBirth)"
        :offer-module-id="getOfferModuleId"
        :allowed-document-types="getOfferAllowedDocuments"
        :document-type="getDocumentTypeId"
        :document-series="document.serialNumber"
        :document-number="document.documentNumber"
        :document-issue-date="formatDate(document.issueDate)"
        :document-issued-by="document.issuingAuthority"
        :document-expiration-date="formatDate(document.expirationDate)"
        @changeDocumentType="onChangeDocumentType"
        @changeDocumentSeries="onChangeDocumentSeries"
        @changeDocumentNumber="onChangeDocumentNumber"
        @changeDocumentIssueDate="onChangeDocumentIssueDate"
        @changeDocumentIssuedBy="onChangeDocumentIssuedBy"
        @changeDocumentExpirationDate="onChangeDocumentExpirationDate"
      />

      <PetForm
        ref="petForm"
        :initial-pet-type="pet.petType"
        :initial-pet-breed="pet.petBreed"
        :initial-pet-sex="pet.petSex"
        :initial-pet-alias="pet.petAlias"
        :initial-pet-birth-date="pet.petBirthDate"
        @changePetType="onChangePetType"
        @changePetBreed="onChangePetBreed"
        @changePetSex="onChangePetSex"
        @changePetAlias="onChangePetAlias"
        @changePetBirthDate="onChangePetBirthDate"
      />

      <MicrochipForm
        ref="microchipForm"
        :initial-microchip-code="pet.microchipCode"
        :initial-microchip-date="pet.microchipDate"
        @changeMicrochipCode="onChangeMicrochipCode"
        @changeMicrochipDate="onChangeMicrochipDate"
      />

      <VetPassportForm
        ref="vetPassportForm"
        :vet-passport-serial="vetPassport.serial"
        :vet-passport-number="vetPassport.number"
        :vet-passport-date="vetPassport.date"
        :vet-passport-issued-by="vetPassport.issuedBy"
        @changeVetPassportSerial="onChangeVetPassportSerial"
        @changeVetPassportNumber="onChangeVetPassportNumber"
        @changeVetPassportDate="onChangeVetPassportDate"
        @changeVetPassportIssuedBy="onChangeVetPassportIssuedBy"
      />

      <div class="pets-policy-details__actions">
        <BlueButton
          class="pets-policy-details__action-button"
          :is-loading="isLoading"
          :is-disabled="!isFormValid"
          @click="onGoToNextStep"
        >
          Перейти до наступного кроку
        </BlueButton>
        <BlueOutlineButton
          class="pets-policy-details__action-button"
          :is-loading="isLoading"
          :is-disabled="!isFormValid"
          @click="onSaveDraft"
        >
          Зберегти чернетку
        </BlueOutlineButton>
      </div>
    </div>

    <FillDataPopup
      ref="fillDataPopup"
      :policyholder-type="policyholder.type"
      :policyholder-data="fillData.policyholder"
      :pets-list="fillData.petsList"
      :documents-list="fillData.documentsList"
      :selected-document-id="document.id"
      :allowed-document-types="getOfferAllowedDocuments"
      @fillPolicyholderData="onFillPolicyholderData"
    />

    <SanctionsAlertModal
      :sanctions-data="getSanctions"
      :is-sanctioned="isSanctioned"
      @set-sanctions="onCloseSanctions"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { default as DocumentForm } from './document-form';
import { default as FillDataPopup } from './fill-data-popup';
import { default as MicrochipForm } from './microchip-form';
import { default as PetForm } from './pet-form';
import { default as PolicyholderForm } from './policyholder-form';
import { default as VetPassportForm } from './vet-passport-form';
import { saveOrderResults } from '@/entities/insurance/config';
import { petTypeIdMap } from '@/entities/insurance/config/pet-types';
import { petSexIdMap } from '@/entities/insurance/config/pet-sex';
import { commonDocumentTypes } from '@/features/selectors';
import { PetsOrderParamsMapper } from '@/entities/insurance/mapper';
import { mapPetsOfferParams } from '@/entities/insurance/mapper';
import { DateFormatter } from '@/shared/lib/calendar';
import { ScrollToFirstError } from '@/shared/lib/form';
import { Client } from '@/shared/lib/client';
import {
  ContractState,
  DocumentLogic,
  DocumentState,
  OfferState,
  OrderState,
  PetsPolicyholderState,
  PetState,
  VetPassportState,
} from '@/shared/lib/insurance';
import { TaxNumberParser } from '@/shared/lib/tax-number';
import { BlueButton, BlueOutlineButton, HelpTooltip, WhiteButton } from '@/shared/ui/form';
import { FillDataIcon } from '@/shared/ui/icons';
import { SanctionsAlertModal } from '@/shared/ui/insurance';

const { formatDate } = DateFormatter();
const { scrollToFirstError } = ScrollToFirstError();
const { extractBirthdayFromTaxNumber } = TaxNumberParser();
const { preparePetsOrderDraft } = PetsOrderParamsMapper();

export default {
  data() {
    return {
      isLoading: false,
      isFormValid: false,
      fillData: {
        policyholder: null,
        petsList: [],
        documentsList: [],
      },
    };
  },
  mounted() {
    this.loadPolicyholderData();
  },
  mixins: [
    Client,
    PetState('petsModule'),
    VetPassportState('petsModule'),
    PetsPolicyholderState('petsModule'),
    ContractState('petsModule'),
    DocumentLogic,
    DocumentState('petsModule'),
    OfferState('petsModule'),
    OrderState('petsModule'),
  ],
  components: {
    PetForm, VetPassportForm, MicrochipForm, PolicyholderForm, DocumentForm,
    BlueButton, BlueOutlineButton, WhiteButton, HelpTooltip, FillDataIcon,
    FillDataPopup, SanctionsAlertModal,
  },
  computed: {
    ...mapGetters('layoutModule', ['windowWidth', 'isMobile']),
    ...mapGetters('policyholderModule', ['isLoadingPolicyholderData']),
    ...mapGetters('petsModule', ['getOffer', 'getCalculatorEarnings']),
    getOfferCompanyName() {
      return this.getOffer?.company?.name || '';
    },
    getOfferAllowedDocuments() {
      const commonIds = commonDocumentTypes.map(doc => doc.id);
      const allowedTypes = this.getOffer?.documents?.allowedTypes;
      if (allowedTypes?.length > 0) {
        return allowedTypes.filter(typeId => commonIds.includes(typeof typeId === 'object' ? typeId.id : typeId));
      }
      return commonIds;
    },
    getOfferModuleId() {
      return this.getOffer?.module?.id || null;
    },
    getDocumentTypeId() {
      return this.normalizeDocumentValue(this.document?.documentType) || null;
    },
    getTaxNumberStatus() {
      return this.getOffer?.company?.taxNumberStatus || false;
    },

    getPolicyholderAddressCity() {
      return this.policyholder.address?.city || null;
    },
    getPolicyholderAddressStreet() {
      return this.policyholder.address?.street?.nameUa || null;
    },
    getPolicyholderAddressHouse() {
      return this.policyholder.address?.buildingNumber || null;
    },
    getPolicyholderAddressApartment() {
      return this.policyholder.address?.apartmentNumber || null;
    },
  },
  methods: {
    ...mapActions('policyholderModule', ['fetchPolicyholder']),
    ...mapActions('clientModule', ['saveClientData']),
    ...mapActions('notificationModule', ['showNotification', 'showNotificationErrorMessage']),
    ...mapActions('petsModule', ['setOffer']),
    formatDate,
    extractBirthdayFromTaxNumber,
    updateStoredClientData() {
      if (!this.policyholder.taxNumber || !this.policyholder.phone || !this.policyholder.email) return;
      const city = this.policyholder.address?.city;
      this.saveClientData({
        productType: 'pets',
        clientId: this.policyholder.id || null,
        userTypeId: 1,
        info: {
          name: this.getFirstNameUa || null,
          surname: this.getLastNameUa || null,
          patronymic: this.getMiddleNameUa || null,
          name_ua: this.getFirstNameUa || null,
          surname_ua: this.getLastNameUa || null,
          patronymic_ua: this.getMiddleNameUa || null,
          dateBirth: this.policyholder.dateOfBirth || null,
          identificationCode: this.policyholder.taxNumber,
          mail: this.policyholder.email,
          phone: this.policyholder.phone,
          city: city ? {
            id: city.id || null,
            name_ua: city.nameUa || city.nameFullNameUa || null,
            region_name_ua: city.region?.nameUa || null,
          } : null,
          street: this.policyholder.address?.street?.nameUa || null,
          house: this.policyholder.address?.buildingNumber || null,
          apartment: this.policyholder.address?.apartmentNumber || null,
          documentation: {
            type: this.document?.documentType || null,
            serial: this.document?.serialNumber || null,
            number: this.document?.documentNumber || null,
            issuedBy: this.document?.issuingAuthority || null,
            dateOfIssue: this.document?.issueDate || null,
            endDateOfIssue: this.document?.expirationDate || null,
          },
        },
        petType: this.pet.petType || null,
        petBreed: this.pet.petBreed || null,
        petSex: this.pet.petSex || null,
        petAlias: this.pet.petAlias || null,
        petBirthDate: this.pet.petBirthDate || null,
        vetPassportSerial: this.vetPassport.serial || null,
        vetPassportNumber: this.vetPassport.number || null,
        vetPassportDate: this.vetPassport.date || null,
        vetPassportIssuedBy: this.vetPassport.issuedBy || null,
        microchipCode: this.pet.microchipCode || null,
        microchipDate: this.pet.microchipDate || null,
      });
    },
    async loadPolicyholderData() {
      const policyholderNeedsLoad = this.policyholder?.id
        && !this.isLoadingPolicyholderData
        && (
          !this.policyholder?.email
          || !this.policyholder?.taxNumber
          || !this.policyholder?.dateOfBirth
          || !this.policyholder?.address?.city
        );

      const documentNeedsLoad = !this.document?.serialNumber
        && !this.document?.documentNumber
        && this.document?.id;

      if (!policyholderNeedsLoad && !documentNeedsLoad) return;

      if (policyholderNeedsLoad || documentNeedsLoad) {
        try {
          const response = await this.fetchPolicyholder(this.policyholder.id);
          this.fillData = {
            policyholder: response,
            petsList: response?.pets || [],
            documentsList: response?.documents || [],
          };

          if (response && !Array.isArray(response)) {
            if (policyholderNeedsLoad) {
              const addressFromResponse = this.convertToAddress(response);

              this.policyholder = {
                ...this.policyholder,
                id: response?.id || this.policyholder.id,
                type: this.policyholder.type || 'natural',
                fullName: {
                  fullNameUa: response?.fio || this.policyholder.fullName?.fullNameUa || null,
                  fullNameEn: response?.fio_en || this.policyholder.fullName?.fullNameEn || null,
                },
                taxNumber: response?.identification_number || this.policyholder.taxNumber || null,
                address: {
                  ...this.policyholder.address,
                  city: addressFromResponse?.city || this.policyholder.address?.city || null,
                  street: addressFromResponse?.street || this.policyholder.address?.street || { nameUa: null, nameEn: null },
                  buildingNumber: addressFromResponse?.buildingNumber || this.policyholder.address?.buildingNumber || null,
                  apartmentNumber: addressFromResponse?.apartmentNumber || this.policyholder.address?.apartmentNumber || null,
                  region: addressFromResponse?.region || this.policyholder.address?.region || null,
                },
                email: response?.email || this.policyholder.email || null,
                phone: response?.phone || this.policyholder.phone || null,
                dateOfBirth: this.formatDate(response?.date_of_birth) || this.policyholder.dateOfBirth || null,
              };
            }

            if (documentNeedsLoad) {
              const document = response?.documents?.find(doc => doc.id === this.document.id);
              if (document) {
                this.document = {
                  ...this.document,
                  id: document?.id || null,
                  documentType: document?.document_type || null,
                  serialNumber: document?.serial || null,
                  documentNumber: document?.number || null,
                  issuingAuthority: document?.issued_by || null,
                  issueDate: this.formatDate(document?.date_of_issue),
                  expirationDate: document?.end_date_of_issue || null,
                };
              }
            }
          }
        } catch (error) {
          this.showNotificationErrorMessage({
            error,
            title: 'Помилка',
            message: 'Помилка завантаження даних страхувальника',
          });
        }
      }
    },
    convertToAddress(response) {
      if (!response) return null;
      const rawCity = response?.city || null;
      return {
        region: null,
        city: rawCity ? {
          id: rawCity.id || null,
          nameUa: rawCity.name_ua || rawCity.nameUa || rawCity.name || null,
          nameFullNameUa: rawCity.name_full_name_ua || rawCity.nameFullNameUa || null,
          region: rawCity.region || null,
        } : null,
        street: response?.street ? { nameUa: response.street } : null,
        buildingNumber: response?.house || null,
        apartmentNumber: response?.apartment || null,
      };
    },
    onOpenFillDataPopup() {
      this.$refs.fillDataPopup.open();
    },
    onFillPolicyholderData({ policyholder, pet, document }) {
      if (policyholder) {
        const addressFromResponse = this.convertToAddress(policyholder);

        this.policyholder = {
          ...this.policyholder,
          id: policyholder?.id || this.policyholder.id,
          fullName: {
            fullNameUa: policyholder?.fio || this.policyholder.fullName?.fullNameUa || null,
            fullNameEn: policyholder?.fio_en || this.policyholder.fullName?.fullNameEn || null,
          },
          taxNumber: policyholder?.identification_number || this.policyholder.taxNumber,
          address: {
            ...this.policyholder.address,
            city: addressFromResponse.city || this.policyholder.address?.city || null,
            street: addressFromResponse.street || this.policyholder.address?.street || { nameUa: null, nameEn: null },
            buildingNumber: addressFromResponse.buildingNumber || this.policyholder.address?.buildingNumber || null,
            apartmentNumber: addressFromResponse.apartmentNumber || this.policyholder.address?.apartmentNumber || null,
            region: addressFromResponse.region || this.policyholder.address?.region || null,
          },
          email: policyholder?.email || this.policyholder.email,
          phone: policyholder?.phone || this.policyholder.phone,
          dateOfBirth: this.formatDate(policyholder?.date_of_birth) || this.policyholder.dateOfBirth,
        };

        this.fillData = {
          policyholder,
          petsList: policyholder?.pets || [],
          documentsList: policyholder?.documents || [],
        };
      }

      if (pet) {
        this.pet = {
          ...this.pet,
          petType: pet?.pet_type != null ? (petTypeIdMap[pet.pet_type] ?? pet.pet_type) : this.pet.petType,
          petBreed: pet?.pet_breed || this.pet.petBreed,
          petSex: pet?.pet_sex != null ? (petSexIdMap[pet.pet_sex] ?? pet.pet_sex) : this.pet.petSex,
          petAlias: pet?.pet_alias || this.pet.petAlias,
          petBirthDate: pet?.pet_birth_date || this.pet.petBirthDate,
          microchipCode: pet?.microchip_code || this.pet.microchipCode,
          microchipDate: pet?.microchip_date || this.pet.microchipDate,
        };

        this.vetPassport = {
          ...this.vetPassport,
          serial: pet?.vet_passport?.serial || this.vetPassport.serial,
          number: pet?.vet_passport?.number || this.vetPassport.number,
          date: pet?.vet_passport?.date || this.vetPassport.date,
          issuedBy: pet?.vet_passport?.issued_by || this.vetPassport.issuedBy,
        };
      }

      if (document) {
        this.document = {
          ...this.document,
          id: document?.id || this.document.id,
          documentType: document?.document_type || this.document.documentType,
          serialNumber: document?.serial || this.document.serialNumber,
          documentNumber: document?.number || this.document.documentNumber,
          issuingAuthority: document?.issued_by || this.document.issuingAuthority,
          issueDate: this.formatDate(document?.date_of_issue) || this.document.issueDate,
          expirationDate: this.formatDate(document?.end_date_of_issue) || this.document.expirationDate,
        };
      }

      this.$nextTick(() => this.checkFormValidity());
    },
    onChangePetType(petType) {
      this.pet.petType = petType;
      this.checkFormValidity();
    },
    onChangePetBreed(petBreed) {
      this.pet.petBreed = petBreed;
      this.checkFormValidity();
    },
    onChangePetSex(petSex) {
      this.pet.petSex = petSex;
      this.checkFormValidity();
    },
    onChangePetAlias(petAlias) {
      this.pet.petAlias = petAlias;
      this.checkFormValidity();
    },
    onChangePetBirthDate(petBirthDate) {
      this.pet.petBirthDate = petBirthDate;
      this.checkFormValidity();
    },
    onChangeVetPassportSerial(serial) {
      this.vetPassport.serial = serial;
      this.checkFormValidity();
    },
    onChangeVetPassportNumber(number) {
      this.vetPassport.number = number;
      this.checkFormValidity();
    },
    onChangeVetPassportDate(date) {
      this.vetPassport.date = date;
      this.checkFormValidity();
    },
    onChangeVetPassportIssuedBy(issuedBy) {
      this.vetPassport.issuedBy = issuedBy;
      this.checkFormValidity();
    },
    onChangeMicrochipCode(code) {
      this.pet.microchipCode = code;
      this.checkFormValidity();
    },
    onChangeMicrochipDate(date) {
      this.pet.microchipDate = date;
      this.checkFormValidity();
    },
    onChangePolicyholder(policyholder) {
      this.fillData = {
        policyholder,
        petsList: policyholder?.pets || [],
        documentsList: policyholder?.documents || [],
      };
      this.onOpenFillDataPopup();
    },
    onChangePolicyholderTaxNumber(taxNumber) {
      const policyholderBirthday = this.extractBirthdayFromTaxNumber(taxNumber);

      if (policyholderBirthday && !this.policyholder.dateOfBirth) {
        this.policyholder.dateOfBirth = this.formatDate(policyholderBirthday);
      }

      this.policyholder.taxNumber = taxNumber;
      this.checkFormValidity();
      this.updateStoredClientData();
    },
    onChangePolicyholderLastName(lastName) {
      this.setLastNameUa(lastName ?? null);
      this.checkFormValidity();
      this.updateStoredClientData();
    },
    onChangePolicyholderFirstName(firstName) {
      this.setFirstNameUa(firstName ?? null);
      this.checkFormValidity();
      this.updateStoredClientData();
    },
    onChangePolicyholderMiddleName(middleName) {
      this.setMiddleNameUa(middleName ?? null);
      this.checkFormValidity();
      this.updateStoredClientData();
    },
    onChangePolicyholderBirthday(birthday) {
      this.policyholder.dateOfBirth = this.formatDate(birthday);
      this.checkFormValidity();
      this.updateStoredClientData();
    },
    onChangePolicyholderAddressCity(city) {
      this.policyholder.address = {
        ...this.policyholder.address,
        city: city ? {
          id: city.id || null,
          nameUa: city.name_ua || city.nameUa || city.name || null,
          nameFullNameUa: city.name_full_name_ua || city.nameFullNameUa || null,
          region: city.region || null,
        } : null,
      };
      this.checkFormValidity();
      this.updateStoredClientData();
    },
    onChangePolicyholderAddressStreet(addressStreet) {
      this.policyholder.address = {
        ...this.policyholder.address,
        street: {
          ...this.policyholder.address.street,
          nameUa: addressStreet,
        },
      };
      this.checkFormValidity();
      this.updateStoredClientData();
    },
    onChangePolicyholderAddressHouse(addressHouse) {
      this.policyholder.address = {
        ...this.policyholder.address,
        buildingNumber: addressHouse,
      };
      this.checkFormValidity();
      this.updateStoredClientData();
    },
    onChangePolicyholderAddressApartment(addressApartment) {
      this.policyholder.address = {
        ...this.policyholder.address,
        apartmentNumber: addressApartment,
      };
      this.checkFormValidity();
      this.updateStoredClientData();
    },
    onChangePolicyholderPhone(phone) {
      this.policyholder.phone = phone;
      this.checkFormValidity();
      this.updateStoredClientData();
    },
    onChangePolicyholderEmail(email) {
      this.policyholder.email = email;
      this.checkFormValidity();
      this.updateStoredClientData();
    },
    onChangeWithoutTaxNumber(withoutTaxNumber) {
      this.policyholder.withoutTaxNumber = withoutTaxNumber;
      if (withoutTaxNumber) {
        this.policyholder.taxNumber = null;
      }
      this.checkFormValidity();
      this.updateStoredClientData();
    },
    onChangeDocumentType(documentType) {
      this.document.documentType = documentType;
      this.checkFormValidity();
      this.updateStoredClientData();
    },
    onChangeDocumentSeries(series) {
      this.document.serialNumber = series;
      this.checkFormValidity();
      this.updateStoredClientData();
    },
    onChangeDocumentNumber(number) {
      this.document.documentNumber = number;
      this.checkFormValidity();
      this.updateStoredClientData();
    },
    onChangeDocumentIssueDate(issueDate) {
      this.document.issueDate = issueDate;
      this.checkFormValidity();
      this.updateStoredClientData();
    },
    onChangeDocumentIssuedBy(issuedBy) {
      this.document.issuingAuthority = issuedBy;
      this.checkFormValidity();
      this.updateStoredClientData();
    },
    onChangeDocumentExpirationDate(expirationDate) {
      this.document.expirationDate = expirationDate;
      this.checkFormValidity();
      this.updateStoredClientData();
    },
    async checkFormValidity() {
      await this.$nextTick();

      const petForm = this.$refs.petForm;
      const vetPassportForm = this.$refs.vetPassportForm;
      const microchipForm = this.$refs.microchipForm;
      const policyholderForm = this.$refs.policyholderForm;
      const documentForm = this.$refs.documentForm;

      const petValid = petForm ? !petForm.$v.$invalid : true;
      const vetPassportValid = vetPassportForm ? !vetPassportForm.$v.$invalid : true;
      const microchipValid = microchipForm ? !microchipForm.$v.$invalid : true;
      const policyholderValid = policyholderForm ? !policyholderForm.$v.$invalid : true;
      const documentValid = documentForm ? !documentForm.$v.$invalid : true;

      this.isFormValid = petValid && vetPassportValid && microchipValid && policyholderValid && documentValid;
    },
    async validateForm() {
      await this.$nextTick();

      const petValid = await (this.$refs.petForm?.validateAll() ?? true);
      const vetPassportValid = await (this.$refs.vetPassportForm?.validateAll() ?? true);
      const microchipValid = await (this.$refs.microchipForm?.validateAll() ?? true);
      const policyholderValid = await (this.$refs.policyholderForm?.validateAll() ?? true);
      const documentValid = this.$refs.documentForm
        ? await this.$refs.documentForm.validateAll()
        : true;

      this.isFormValid = petValid && vetPassportValid && microchipValid && policyholderValid && documentValid;

      if (!this.isFormValid) {
        await scrollToFirstError(this.$el);
      }

      return this.isFormValid;
    },
    buildOrderDraftPayload() {
      return preparePetsOrderDraft({
        petType: this.pet.petType || 'cat',
        petBreed: this.pet.petBreed,
        petSex: this.pet.petSex || 'male',
        petAlias: this.pet.petAlias,
        petBirthDate: this.pet.petBirthDate || null,
        vetPassportSerial: this.vetPassport.serial,
        vetPassportNumber: this.vetPassport.number,
        vetPassportDate: this.vetPassport.date || null,
        vetPassportIssuedBy: this.vetPassport.issuedBy,
        microchipCode: this.pet.microchipCode,
        microchipDate: this.pet.microchipDate || null,
        insurancePeriod: this.contract?.estimatedPeriod || null,
        earnings: this.getCalculatorEarnings || null,
        startDate: this.contract?.startDate || null,
        policyholder: this.policyholder,
        document: {
          ...this.document,
          type: this.document.documentType,
          number: this.document.documentNumber,
        },
        offerInfo: mapPetsOfferParams(this.getOffer),
        orderId: this.orderId,
      });
    },
    async onGoToNextStep() {
      this.isLoading = true;
      try {
        const isValid = await this.validateForm();
        if (!isValid) {
          return;
        }

        await this.proceedToNextStep();
      } catch (error) {
        this.showNotificationErrorMessage({
          error,
          title: 'Помилка',
          message: 'Помилка переходу до наступного кроку',
        });
      } finally {
        this.isLoading = false;
      }
    },
    async proceedToNextStep() {
      const orderDraft = this.buildOrderDraftPayload();
      const result = await this.saveOrder(orderDraft);

      if (!result) {
        this.showNotificationErrorMessage({
          title: 'Помилка',
          message: 'Не вдалося зберегти замовлення',
        });
        return;
      }

      if (result.result === saveOrderResults.SANCTIONED) {
        return;
      }

      if (result.result === saveOrderResults.BLOCKED) {
        return;
      }

      if (result.result === saveOrderResults.BLACKLIST) {
        return;
      }

      if (result.result === saveOrderResults.ERROR_MESSAGE || result.result === saveOrderResults.RESPONSE_MESSAGE) {
        this.showNotificationErrorMessage({
          title: 'Помилка',
          message: result.message || 'Сталася помилка',
        });
        return;
      }

      this.$router.push({ name: 'petsFinalization' });
    },
    async onSaveDraft() {
      this.isLoading = true;
      try {
        const isValid = await this.validateForm();
        if (!isValid) {
          return;
        }

        const orderDraft = this.buildOrderDraftPayload();
        const result = await this.saveOrder(orderDraft);

        if (result?.orderId) {
          this.orderId = result.orderId;
        }

        this.showNotification({
          type: 'success',
          title: 'Успішно',
          message: 'Чернетку успішно збережено',
        });
      } catch (error) {
        this.showNotificationErrorMessage({
          error,
          title: 'Помилка',
          message: 'Помилка збереження чернетки',
        });
      } finally {
        this.isLoading = false;
      }
    },
    onCloseSanctions(data) {
      this.setSanctions(data);
    },
  },
};
</script>

<style scoped lang="scss">
.pets-policy-details {
  &__layout {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  &__fill-data {
    margin-bottom: 16px;

    &__icon {
      margin-right: 8px;
    }
  }

  &__actions {
    display: flex;
    gap: 16px;
    margin-top: 8px;

    @media (max-width: 768px) {
      flex-direction: column;
    }
  }

  &__action-button {
    min-width: 200px;

    @media (max-width: 768px) {
      min-width: auto;
    }
  }
}
</style>
